import type { ShareRequest, ShareResult, SharingAdapter } from '../interfaces/sharing.js';

function canUseNativeShare(): boolean {
  if (typeof navigator === 'undefined' || !('share' in navigator)) {
    return false;
  }

  const { protocol, hostname } = window.location;
  return protocol === 'https:' || hostname === 'localhost' || hostname === '127.0.0.1';
}

export function createWebSharingAdapter(): SharingAdapter {
  return {
    canNativeShare() {
      return canUseNativeShare();
    },

    async share(request: ShareRequest): Promise<ShareResult> {
      const hasPayload = Boolean(request.url || request.text);
      if (!hasPayload) {
        return 'unavailable';
      }

      if (canUseNativeShare()) {
        try {
          const shareData: ShareData = {};
          if (request.title) shareData.title = request.title;
          if (request.text) shareData.text = request.text;
          if (request.url) shareData.url = request.url;
          await navigator.share(shareData);
          return 'shared';
        } catch (error) {
          if (error instanceof Error && error.name === 'AbortError') {
            return 'cancelled';
          }
        }
      }

      if (typeof navigator === 'undefined' || !navigator.clipboard?.writeText) {
        return 'unavailable';
      }

      const clipboardText =
        request.text && request.url
          ? `${request.text}\n${request.url}`
          : request.text || request.url || '';

      try {
        await navigator.clipboard.writeText(clipboardText);
        return 'copied';
      } catch {
        return 'unavailable';
      }
    },
  };
}
